/*
  Dip <dip-quotes> — the buy-column review carousel, shared by the PDP buy module
  and the homepage bundle builder through snippets/dip-buy-quotes.liquid.

  It lived in dip-home.js, which the PDPs do not load. The PDP buy column renders
  the snippet anyway, so its carousel only ever worked because some OTHER section
  further down the page (comparison, do-more, reviews) happened to pull dip-home.js
  in for its tabs. Removing those tags would have killed the carousel silently, so
  the element moves here: shared markup deserves a shared module, not a script that
  arrives by luck.

  Auto-rotates only while visible and only until the reader takes over.
*/

class DipQuotes extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[ref="quotesTrack"]') || this.querySelector('.dip-quotes-track');
    if (!track) return;
    const slides = [...track.querySelectorAll('.dip-quotes-slide')];
    if (slides.length < 2) return;

    const dotsWrap = this.querySelector('[ref="quotesDots"]');
    const dots = [];
    if (dotsWrap) {
      slides.forEach((_, di) => {
        const d = document.createElement('span');
        if (di === 0) d.className = 'is-on';
        dotsWrap.appendChild(d);
        dots.push(d);
      });
    }

    let i = 0, timer = null, userDriven = false, advanced = false;
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((sl, si) => {
        const on = si === i;
        sl.classList.toggle('is-on', on);
        sl.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((d, di) => d.classList.toggle('is-on', di === i));
    };
    const stop = () => { if (timer) { clearTimeout(timer); timer = null; } };
    // First advance is quick (1s) so it's instantly obvious there are more reviews; then every 3s.
    const tick = () => { advanced = true; show(i + 1); timer = setTimeout(tick, 3000); };
    const start = () => {
      if (userDriven || timer || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = setTimeout(tick, advanced ? 3000 : 1000);
    };
    // Once user-driven, announce changes (aria-live stays off during auto-rotation to avoid SR spam).
    const takeOver = () => { userDriven = true; stop(); track.setAttribute('aria-live', 'polite'); };

    this.querySelector('[ref="quotesPrev"]')?.addEventListener('click', () => { takeOver(); show(i - 1); });
    this.querySelector('[ref="quotesNext"]')?.addEventListener('click', () => { takeOver(); show(i + 1); });
    this.addEventListener('mouseenter', stop);
    this.addEventListener('mouseleave', start);
    this.addEventListener('focusin', stop);
    this.addEventListener('focusout', start);

    // Swipe: horizontal drags flip the quote (track has touch-action:pan-y, so
    // vertical page scroll still wins); mostly-vertical gestures are ignored.
    let x0 = null, y0 = null;
    track.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; }, { passive: true });
    track.addEventListener('pointercancel', () => { x0 = null; });
    track.addEventListener('pointerup', (e) => {
      if (x0 == null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) { takeOver(); show(dx < 0 ? i + 1 : i - 1); }
    });

    // Rotate only while visible (also serves as the initial start()).
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => entries.forEach((en) => (en.isIntersecting ? start() : stop())),
        { threshold: 0.4 }
      ).observe(this);
    } else start();
  }
}

if (!customElements.get('dip-quotes')) customElements.define('dip-quotes', DipQuotes);

export { DipQuotes };
